import React, { useEffect, useState } from 'react';
import { BackupOutlined } from '@mui/icons-material';
import { Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

// Choose which website tools to back up; their data is saved to S3
const ToolBackupCreate = () => {
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api.getBackupTools()
      .then((response) => { if (active) setTools(response?.data?.data || []); })
      .catch(() => { if (active) setError('Tools could not be loaded.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const toggle = (key) => {
    setSelected((current) => current.includes(key) ? current.filter((k) => k !== key) : [...current, key]);
  };

  const closePasswordDialog = () => {
    if (!saving) {
      setPasswordOpen(false);
      setPassword('');
    }
  };

  const handleCreateBackup = async (event) => {
    event.preventDefault();
    if (!password.trim() || !selected.length) return;
    setSaving(true);
    try {
      const response = await api.createToolBackups(password, selected);
      toast.success(response?.data?.data?.message || 'Tool backups created successfully.');
      setPasswordOpen(false);
      setPassword('');
      navigate('/backup?tab=tools');
    } catch (requestError) {
      toast.error(requestError?.response?.data?.message || 'Could not create the tool backup.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Button variant="text" onClick={() => navigate('/backup?tab=tools')} sx={{ minWidth: 0, p: 0, textTransform: 'none', color: '#1473E6', fontSize: 15 }}>Tool Backups</Button>
        <Typography sx={{ color: '#98A2B3' }}>/</Typography>
        <Typography sx={{ color: '#344054', fontSize: 15 }}>Create Tool Backup</Typography>
      </Box>
      <Typography sx={{ color: '#1D1F2C', fontSize: { xs: 28, md: 32 }, fontWeight: 700, lineHeight: 1.25, mb: 0.75 }}>Tool Backup</Typography>
      <Typography sx={{ color: '#667085', fontSize: 16, mb: 3 }}>Select one or more tools to save all of their current enquiries and orders to S3. A new backup of a tool replaces its previous backup.</Typography>

      <Paper variant="outlined" sx={{ borderColor: '#E0E2E7', borderRadius: 2, overflow: 'hidden', boxShadow: '0 3px 10px rgba(16, 24, 40, 0.04)' }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ color: '#1D1F2C', fontSize: 16, fontWeight: 700 }}>Choose tools for backup</Typography>
            <Chip label={`${selected.length} selected`} size="small" sx={{ color: '#512DA8', backgroundColor: '#F3EEFF', fontWeight: 700 }} />
          </Box>
          <Button variant="contained" disabled={!selected.length} startIcon={<BackupOutlined />} onClick={() => setPasswordOpen(true)} sx={{ textTransform: 'none', backgroundColor: '#512DA8', '&:hover': { backgroundColor: '#432584' } }}>Create backup</Button>
        </Box>
        <TableContainer sx={{ borderTop: '1px solid #EAECF0' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead><TableRow sx={{ backgroundColor: '#F9FAFB' }}>
              <TableCell padding="checkbox" sx={{ pl: 2 }}>
                <Checkbox
                  checked={tools.length > 0 && selected.length === tools.length}
                  indeterminate={selected.length > 0 && selected.length < tools.length}
                  onChange={() => setSelected(selected.length === tools.length ? [] : tools.map((t) => t.key))}
                />
              </TableCell>
              <TableCell sx={{ width: '30%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Tool</TableCell>
              <TableCell sx={{ color: '#475467', fontWeight: 700, fontSize: 14 }}>What will be saved</TableCell>
              <TableCell sx={{ width: '18%', color: '#475467', fontWeight: 700, fontSize: 14 }}>Records to backup</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {tools.map((tool) => (
                <TableRow key={tool.key} hover onClick={() => toggle(tool.key)} sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 }, '&:hover': { backgroundColor: '#FCFAFF' } }}>
                  <TableCell padding="checkbox" sx={{ pl: 2 }} onClick={(event) => event.stopPropagation()}>
                    <Checkbox checked={selected.includes(tool.key)} onChange={() => toggle(tool.key)} />
                  </TableCell>
                  <TableCell sx={{ py: 2.1, color: '#1D2939', fontWeight: 700, fontSize: 15 }}>{tool.name}</TableCell>
                  <TableCell sx={{ py: 2.1 }}>
                    {tool.parts.map((part) => (
                      <Typography key={part.collection} sx={{ fontSize: 13, color: '#475467' }}>
                        {part.label}: <b style={{ color: '#1D2939' }}>{part.count}</b>
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell sx={{ py: 2.1 }}>
                    <Chip label={`${tool.recordCount} records`} size="small" sx={{ minWidth: 92, color: tool.recordCount ? '#512DA8' : '#667085', backgroundColor: tool.recordCount ? '#F3EEFF' : '#F2F4F7', fontWeight: 700 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid #EAECF0', background: '#FCFCFD' }}>
          <Typography sx={{ fontSize: 13, color: '#667085' }}>
            The Cross Reference tool searches your products and keeps no data of its own, so it is covered by the product backups.
          </Typography>
        </Box>
      </Paper>

      <Dialog open={passwordOpen} onClose={closePasswordDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box component="form" onSubmit={handleCreateBackup}>
          <Box sx={{ px: 4, pt: 3.5, pb: 2, background: 'linear-gradient(135deg, #FAF8FF 0%, #FFFFFF 72%)', borderBottom: '1px solid #EEE9FA' }}>
            <Typography sx={{ color: '#1D1F2C', fontWeight: 700, fontSize: 23 }}>Create secure backup</Typography>
            <Typography sx={{ color: '#667085', fontSize: 15, lineHeight: 1.55, mt: 0.75 }}>Confirm your password to save the selected tools' data.</Typography>
          </Box>
          <DialogContent sx={{ px: 4, pt: '24px !important', pb: 1.5 }}>
            <Typography component="label" htmlFor="tool-backup-admin-password" sx={{ display: 'block', color: '#344054', fontWeight: 600, fontSize: 14, mb: 1 }}>Admin password</Typography>
            <TextField
              id="tool-backup-admin-password"
              fullWidth
              autoFocus
              required
              type="password"
              placeholder="Enter your admin password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, minHeight: 54, backgroundColor: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 4, pt: 2.5, pb: 3.5, gap: 1 }}>
            <Button onClick={closePasswordDialog} disabled={saving} sx={{ minWidth: 92, textTransform: 'none', color: '#475467', fontWeight: 600 }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving || !password.trim()} startIcon={<BackupOutlined />} sx={{ minWidth: 164, py: 1.1, textTransform: 'none', fontWeight: 700, backgroundColor: '#512DA8', '&:hover': { backgroundColor: '#432584' } }}>{saving ? 'Creating backup…' : 'Create backup'}</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ToolBackupCreate;
